import { useCallback } from "react";
import { TextField } from "@mui/material";

import { BoundsPreviewMap } from "@/features/widget-forms/forms/weather-model-map/components/bounds-preview-map";
import CheckCard, {
  StatusLabel,
} from "@/features/widget-forms/forms/weather-model-map/components/check-card/check-card";
import {
  initialViewportPresets,
  viewportBoundsEqual,
} from "@/features/widget-forms/shared/initial-viewport-presets";
import type { ParametersGeoBoundingBox } from "@/lib/api";
import panelStyles from "@/features/widget-forms/forms/weather-model-map/components/initial-viewport-tab/initial-viewport-tab.module.css";

const PRESET_KEYS = ["nl", "bes"] as const;

export type InitialViewportPanelStrings = {
  title: string;
  subtitle: string;
  manualTitle: string;
  northLatitude: string;
  westLongitude: string;
  eastLongitude: string;
  southLatitude: string;
  presetsTitle: string;
  presetNl: string;
  presetNlDescription: string;
  presetBes: string;
  presetBesDescription: string;
  selectedStatusText: string;
};

type InitialViewportPanelProps = {
  bounds: ParametersGeoBoundingBox;
  baseLayerStyle: string;
  onBoundsChange: (bounds: ParametersGeoBoundingBox) => void;
  onCoordChange: (
    field: keyof Omit<ParametersGeoBoundingBox, "type">,
    rawValue: unknown,
  ) => void;
  strings: InitialViewportPanelStrings;
};

export function InitialViewportPanel({
  bounds,
  baseLayerStyle,
  onBoundsChange,
  onCoordChange,
  strings,
}: InitialViewportPanelProps) {
  const selectedPreset = PRESET_KEYS.find((key) =>
    viewportBoundsEqual(initialViewportPresets[key], bounds),
  );

  const handlePresetSelect = useCallback(
    (key: (typeof PRESET_KEYS)[number]) => {
      onBoundsChange(initialViewportPresets[key]);
    },
    [onBoundsChange],
  );

  const presetCopy: Record<
    (typeof PRESET_KEYS)[number],
    { title: string; description: string }
  > = {
    nl: { title: strings.presetNl, description: strings.presetNlDescription },
    bes: { title: strings.presetBes, description: strings.presetBesDescription },
  };

  const selectedStatus: StatusLabel = {
    text: strings.selectedStatusText,
    icon: "check",
  };

  return (
    <div className={panelStyles.tabContentWrapper}>
      <div className={panelStyles.tabHeader}>
        <h2 className={panelStyles.tabTitle}>{strings.title}</h2>
        <p className={panelStyles.tabSubtitle}>{strings.subtitle}</p>
      </div>
      <div className={panelStyles.section}>
        <h3 className={panelStyles.sectionTitleCoord}>{strings.manualTitle}</h3>
        <div className={panelStyles.coordGrid}>
          <TextField
            className={panelStyles.coordNorth}
            label={strings.northLatitude}
            type="number"
            inputProps={{ step: 0.1 }}
            value={bounds.north_latitude}
            onChange={(e) => onCoordChange("north_latitude", e.target.value)}
            variant="standard"
            fullWidth
            sx={{ mt: 2 }}
          />
          <TextField
            className={panelStyles.coordWest}
            label={strings.westLongitude}
            type="number"
            inputProps={{ step: 0.1 }}
            value={bounds.west_longitude}
            onChange={(e) => onCoordChange("west_longitude", e.target.value)}
            variant="standard"
            fullWidth
            sx={{ mt: 2 }}
          />
          <TextField
            className={panelStyles.coordEast}
            label={strings.eastLongitude}
            type="number"
            inputProps={{ step: 0.1 }}
            value={bounds.east_longitude}
            onChange={(e) => onCoordChange("east_longitude", e.target.value)}
            variant="standard"
            fullWidth
            sx={{ mt: 2 }}
          />
          <TextField
            className={panelStyles.coordSouth}
            label={strings.southLatitude}
            type="number"
            inputProps={{ step: 0.1 }}
            value={bounds.south_latitude}
            onChange={(e) => onCoordChange("south_latitude", e.target.value)}
            variant="standard"
            fullWidth
            sx={{ mt: 2 }}
          />
        </div>
      </div>
      <div className={panelStyles.section}>
        <h3 className={panelStyles.sectionTitle}>{strings.presetsTitle}</h3>
        <div className={panelStyles.presetGrid}>
          {PRESET_KEYS.map((key) => (
            <CheckCard
              key={key}
              title={presetCopy[key].title}
              checked={selectedPreset === key}
              statusLabel={selectedPreset === key ? selectedStatus : undefined}
              onClick={() => handlePresetSelect(key)}
              className={panelStyles.presetCard}
            >
              {presetCopy[key].description}
            </CheckCard>
          ))}
        </div>
      </div>
      <div className={panelStyles.boundsPreviewInTab}>
        <BoundsPreviewMap
          bounds={bounds}
          fitBoundsPadding={40}
          baseLayerStyle={baseLayerStyle}
          onBoundsChange={onBoundsChange}
          boundsInteractionMode="editable"
        />
      </div>
    </div>
  );
}
